import i18n from "i18next";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { useToast } from "@/components/shared/Toast";
import { destination, toToast } from "@/lib/notifications";
import { useNotifications } from "./useNotifications";

/**
 * Toasts for notifications that arrive while you're in the app (Olivia, 10 Oct):
 * six seconds, with Open. Mounted once for the signed-in app, so a phone inside
 * a document (no bell there) still gets them. What was already there when the
 * app opened doesn't toast: the bell holds it.
 */
export function NotificationToaster() {
  const { items, markRead } = useNotifications();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const seenUntil = useRef<number | null>(null);

  useEffect(() => {
    if (!items.length && seenUntil.current === null) return;
    const newest = items.reduce((t, n) => Math.max(t, Date.parse(n.createdAt)), 0);
    if (seenUntil.current === null) {
      seenUntil.current = newest;
      return;
    }
    for (const n of toToast(items, seenUntil.current)) {
      const to = destination(n);
      showToast(n.title, "info", {
        durationMs: 6000,
        action: to
          ? {
              key: "open-notification",
              label: i18n.t("shell:notifications.open"),
              onClick: () => {
                void markRead([n.id]);
                navigate(to);
              },
            }
          : undefined,
      });
    }
    seenUntil.current = Math.max(seenUntil.current, newest);
  }, [items, showToast, navigate, markRead]);

  return null;
}
